"use server";

import { redirect } from "next/navigation";
import { eq } from "drizzle-orm";
import { getDb } from "@/lib/db/client";
import { users } from "@/lib/db/schema";
import { DEFAULT_PRIVACY_LOCALE, PRIVACY_VERSION } from "@/lib/legal";
import { hashPassword, verifyPassword } from "@/lib/password";
import { getSession } from "@/lib/session";
import { sanitizeInternalPath } from "@/lib/nav";
import { loginFormSchema, registerFormSchema } from "@/lib/validation/auth";

export type AuthActionState = {
  error?: string;
  fieldErrors?: Record<string, string>;
} | null;

function formString(formData: FormData, key: string): string {
  const v = formData.get(key);
  return typeof v === "string" ? v : "";
}

export async function registerAction(
  _prev: AuthActionState,
  formData: FormData,
): Promise<AuthActionState> {
  const privacyAccepted = formData.get("privacyAccepted");
  if (privacyAccepted !== "true") {
    return {
      error:
        "Bitte die Datenschutzerklärung lesen und der Verarbeitung zustimmen.",
    };
  }

  const raw = {
    email: formString(formData, "email"),
    password: formString(formData, "password"),
    displayName: formString(formData, "displayName"),
    rsvp: formString(formData, "rsvp"),
    notes: formString(formData, "notes"),
    phone: formString(formData, "phone"),
  };

  const parsed = registerFormSchema.safeParse(raw);
  if (!parsed.success) {
    const fieldErrors: Record<string, string> = {};
    for (const issue of parsed.error.issues) {
      const path = issue.path[0];
      if (typeof path === "string" && !fieldErrors[path]) {
        fieldErrors[path] = issue.message;
      }
    }
    return { fieldErrors };
  }

  const data = parsed.data;
  const passwordHash = hashPassword(data.password);
  const privacyAcceptedAt = new Date();

  const db = getDb();

  try {
    db.transaction((tx) => {
      tx.insert(users).values({
        email: data.email.toLowerCase(),
        passwordHash,
        displayName: data.displayName,
        rsvp: data.rsvp,
        notes: data.notes ?? null,
        phone: data.phone ?? null,
        privacyVersion: PRIVACY_VERSION,
        privacyAcceptedAt,
        privacyLocale: DEFAULT_PRIVACY_LOCALE,
      }).run();
    });
  } catch (e: unknown) {
    const code =
      typeof e === "object" && e !== null && "code" in e
        ? String((e as { code: unknown }).code)
        : "";
    const msg = e instanceof Error ? e.message : "";
    if (
      code === "SQLITE_CONSTRAINT_UNIQUE" ||
      msg.includes("UNIQUE constraint failed")
    ) {
      return { error: "Diese E-Mail ist bereits registriert." };
    }
    console.error(e);
    return { error: "Registrierung fehlgeschlagen. Bitte später erneut versuchen." };
  }

  const session = await getSession();
  const row = db
    .select({ id: users.id })
    .from(users)
    .where(eq(users.email, data.email.toLowerCase()))
    .get();

  if (!row) {
    return { error: "Benutzer konnte nicht geladen werden." };
  }

  session.userId = row.id;
  await session.save();

  redirect("/dashboard");
}

export async function loginAction(
  _prev: AuthActionState,
  formData: FormData,
): Promise<AuthActionState> {
  const raw = {
    email: formString(formData, "email"),
    password: formString(formData, "password"),
  };

  const parsed = loginFormSchema.safeParse(raw);
  if (!parsed.success) {
    const fieldErrors: Record<string, string> = {};
    for (const issue of parsed.error.issues) {
      const path = issue.path[0];
      if (typeof path === "string" && !fieldErrors[path]) {
        fieldErrors[path] = issue.message;
      }
    }
    return { fieldErrors };
  }

  const db = getDb();
  const user = db
    .select()
    .from(users)
    .where(eq(users.email, parsed.data.email.toLowerCase()))
    .get();

  if (!user || !verifyPassword(parsed.data.password, user.passwordHash)) {
    return { error: "E-Mail oder Passwort ist ungültig." };
  }

  const session = await getSession();
  session.userId = user.id;
  await session.save();

  redirect(sanitizeInternalPath(formString(formData, "next")));
}

export async function logoutAction() {
  const session = await getSession();
  session.destroy();
  redirect("/");
}
